import React, { useEffect, useState } from 'react';
import { CircularProgress, Grid, IconButton } from '@mui/material';
import { CloudUpload, Cancel } from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import SoftBox from "components/SoftBox";
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import { baseURL } from 'utils/api';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

const ImageTypes = {
  IMAGE: 'image',
};

const DraggableImage = ({ image, index, moveImage, handleCancel }) => {
  const [, ref] = useDrag({
    type: ImageTypes.IMAGE,
    item: { index },
  });

  const [, drop] = useDrop({
    accept: ImageTypes.IMAGE,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveImage(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div ref={(node) => ref(drop(node))} style={{ position: 'relative' }}>
      <img
        src={image}
        alt={`uploaded-image-${index}`}
        width="100"
        height="100"
        style={{
          objectFit: 'cover',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
        }}
      />
      <IconButton
        onClick={() => handleCancel(index)}
        sx={{
          position: 'absolute',
          top: '-4px',
          right: '-10px',
          color: 'white',
          backgroundColor: '#ffffff',
          padding: '1px',
        }}
      >
        <Cancel />
      </IconButton>
    </div>
  );
};

const ImageGallery = ({ data, id, handelRefresh,refresh }) => {
  const userTheme = useSelector((state) => state.commonSlice.theme);
  const [images, setImages] = useState([]);
  const [newImage, setNewImage] = useState([]);
  const [madeChanges, setMadeChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    if (images.length + files.length > 5) {
      toast.error("You can only upload a maximum of 5 images.");
      return;
    }

    if (files.length > 0) {
      const mainFiles = [...images];
      files.forEach((file, i) => {
        mainFiles.push({
          url: file,
          currentIndex: images.length + i,
          defaultIndex: images.length + i
        })
      });
      setImages(mainFiles);
      setNewImage(files);
    }
    setMadeChanges(true);
  };


  const handleCancel = (index) => {

    const image = images[index];
    setMadeChanges(true);
    const removeImage = images.filter((_, idx) => idx !== index);
    const updateIndex = removeImage.map((item, index) => ({ ...item, currentIndex: index }));
    setImages(updateIndex);

    if (typeof (image?.url) == 'string') {
      try {
        fetch(`${baseURL}/profile/deleteImage?id=${id}&index=${index}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }).then(response => response.json())
          .then(data => {
            if (data.status) {
              toast.success(data.message);
            } else {
              toast.error(data.message);
            }
          }
          )
      } catch (error) {
        toast.error(error.message || 'Something went wrong');
      }
    }

  };
  

  const moveImage = (fromIndex, toIndex) => {

    // Update `originalData` and swap indices
    const updatedOriginalData = [...images];
    // Swap `currentIndex` in `originalData`
    const [movedItem] = updatedOriginalData.splice(fromIndex, 1);
    updatedOriginalData.splice(toIndex, 0, movedItem);
    // Update `currentIndex` for affected items
    updatedOriginalData[toIndex].currentIndex = toIndex;
    if (fromIndex < toIndex) {
      // Shift indices for items between `fromIndex` and `toIndex`
      for (let i = fromIndex; i < toIndex; i++) {
        updatedOriginalData[i].currentIndex = i;
      }
    } else {
      // Shift indices for items between `toIndex` and `fromIndex`
      for (let i = toIndex + 1; i <= fromIndex; i++) {
        updatedOriginalData[i].currentIndex = i;
      }
    }
    // Update the originalData state
    setImages(updatedOriginalData);
    setMadeChanges(true);

  };


  const handleReset = (e) => {
    e.preventDefault();
    setMadeChanges(true);
    fetch(`${baseURL}/profile/resetImages?id=${id}`).then(response => response.json())
      .then(data => {
        if (data.status) {
          handelRefresh()
          toast.success(data.message);
        } else {
          toast.error(data.message);
        }
      }).catch(error => {
        console.error('Error:', error);
      });
  };


  useEffect(() => {
    setImages(data);
  }, [data]);

  useEffect(() => {
    handelRefresh();
  }, []);


  const handleSaveChanges = (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();

    images.forEach((image, i) => {
      if (typeof (image.url) == 'string') {
        formData.append(`images[${i}][url]`, image?.url);
        formData.append(`images[${i}][currentIndex]`, image?.currentIndex);
        formData.append(`images[${i}][defaultIndex]`, image?.defaultIndex);
      } else {
        formData.append(`images[${i}][newImage]`, true);
        formData.append(`images[${i}][currentIndex]`, image?.currentIndex);
        formData.append(`images[${i}][defaultIndex]`, image?.defaultIndex);
      }
    });

    newImage && newImage.forEach((image, i) => {
      formData.append('newImageAtDragAndDrop', image);
    });

    fetch(`${baseURL}/profile/dragAndDropImage?id=${id}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData
    }).then(response => response.json())
      .then(data => {
        setLoading(false);
        if (data.status) {
          handelRefresh()
          handelRefresh()
          toast.success(data.message);
        } else {
          toast.error(data.message);
        }
      })
      .catch(error => {
        setLoading(false);
        console.error('Error:', error);
      });
  }


  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setMadeChanges(true);

    const droppedFiles = Array.from(event.dataTransfer.files);
    const mainFiles = [...images];

    droppedFiles.forEach((file, i) => {
      mainFiles.push({
        url: file,
        currentIndex: images.length + i,
        defaultIndex: images.length + i
      })
    });

    setImages(mainFiles);
    setNewImage(droppedFiles);
  };



  return (
    <DndProvider backend={HTML5Backend}>
      <SoftBox
        sx={{
          padding: '24px',
          borderRadius: '8px',
          margin: 'auto',
          marginTop: '20px',
          // height: '500px',
          width: "100%",
        }}
      >

        <SoftTypography variant="h6" sx={{ marginBottom: 2 }}>
          Upload Your Business Images (max 5 images, max size 10MB)
        </SoftTypography>

        <SoftTypography sx={{ marginBottom: 2 }} variant="caption">
          Drag your images here or click the upload button to browse from your computer.
        </SoftTypography>

        <SoftBox
          sx={{
            border: '2px dashed #ccc',
            padding: '46px',
            textAlign: 'center',
            marginBottom: 2,
            borderRadius: '8px',
          }}
          onDrop={handleDrop}
        >
          <IconButton
            component="label"
            sx={{
              backgroundColor: '#00bcd4',
              color: 'white',
              padding: '12px',
              borderRadius: '50%',
            }}
          >
            <CloudUpload />

            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                handleImageChange(e); // Call your image handling function
                e.target.value = ""; // Reset the input value
              }}
            />

          </IconButton>
        </SoftBox>

        <Grid container spacing={2} justifyContent="center" sx={{ marginBottom: 3 }}>
          {images && images.length > 0 && images
            .sort((a, b) => a.currentIndex - b.currentIndex)
            .map((image, index) => (
              <Grid item key={index}>
                <DraggableImage
                  image={image?.url ? (typeof (image?.url) == 'string' ? image?.url : URL.createObjectURL(image?.url)) : image}
                  index={index}
                  moveImage={moveImage}
                  handleCancel={handleCancel}
                />
              </Grid>
            ))}

        </Grid>

        <SoftBox sx={{ display: 'flex', justifyContent: 'space-between' }} gap={2}>
          <SoftButton variant="outlined"
            fullWidth onClick={handleReset}
           sx={{ color: userTheme === 'dark' ? '#ffffff' : '#000000',
              border : userTheme === 'dark' ? '1px solid #ffffff' : '1px solid #000000'
     
           }}

            >
            Reset
          </SoftButton>
          <SoftButton variant="gradient"
            color={madeChanges ? "info" : "light"}
            disabled={!madeChanges}
            fullWidth onClick={handleSaveChanges}>
            {
              loading ? <CircularProgress size={24} color="white" /> : 'Save Changes'
            }
          </SoftButton>

        </SoftBox>
      </SoftBox>
    </DndProvider>
  );
};

export default ImageGallery;
